import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpLeft,
  BookOpen,
  Check,
  CheckCheck,
  ChevronLeft,
  Clock3,
  HeartHandshake,
  Leaf,
  LoaderCircle,
  LockKeyhole,
  MessageCircle,
  Send,
  ShieldCheck,
  Sparkles,
  Sprout,
  UserRound,
  UsersRound,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ambassadors } from "../services/ambassadors";
import type {
  AmbassadorApplication,
  AmbassadorWeeklyHours,
} from "../types/ambassadors";
import { buildLoginUrl } from "../utils/redirectUtils";
import "./AmbassadorJoin.css";

type ApplicationForm = {
  fullName: string;
  city: string;
  occupation: string;
  telegramUsername: string;
  weeklyHours: AmbassadorWeeklyHours | "";
  motivation: string;
  experience: string;
  acceptedTerms: boolean;
};
type FieldErrors = Partial<Record<keyof ApplicationForm, string>>;
const emptyForm: ApplicationForm = {
  fullName: "",
  city: "",
  occupation: "",
  telegramUsername: "",
  weeklyHours: "",
  motivation: "",
  experience: "",
  acceptedTerms: false,
};
const hours: { value: AmbassadorWeeklyHours; label: string }[] = [
  { value: "UP_TO_3", label: "تا ۳ ساعت" },
  { value: "THREE_TO_SEVEN", label: "۳ تا ۷ ساعت" },
  { value: "OVER_SEVEN", label: "بیشتر از ۷ ساعت" },
];
const steps = [
  {
    title: "خودت را معرفی کن",
    text: "از علاقه‌ها، تجربه‌ها و زمانی که برای همراهی داری بگو.",
  },
  {
    title: "با هم گفت‌وگو می‌کنیم",
    text: "تیم سفیران درخواستت را بررسی می‌کند و نتیجه را همین‌جا می‌بینی.",
  },
  {
    title: "مسیر همراهی را شروع کن",
    text: "پس از تأیید، پرونده سفیری و مراحل آشنایی در دسترس توست.",
  },
];
const dateLabel = (value: string) =>
  new Date(value).toLocaleDateString("fa-IR", {
    timeZone: "Asia/Tehran",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
function apiMessage(error: any): string {
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (Array.isArray(message) && typeof message[0] === "string")
    return message[0];
  return "ارتباط با سرور برقرار نشد. کمی بعد دوباره تلاش کن؛ اطلاعات فرم حفظ شده است.";
}
function restoreForm(key: string, defaults: ApplicationForm): ApplicationForm {
  try {
    const stored = JSON.parse(sessionStorage.getItem(key) || "null");
    if (!stored || typeof stored !== "object") return defaults;
    const result = { ...defaults };
    (Object.keys(defaults) as (keyof ApplicationForm)[]).forEach((field) => {
      if (field !== "acceptedTerms" && typeof stored[field] === "string") {
        (result[field] as string) = stored[field];
      }
    });
    if (!hours.some((hour) => hour.value === result.weeklyHours))
      result.weeklyHours = "";
    return result;
  } catch {
    return defaults;
  }
}

const AmbassadorJoin: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [form, setForm] = useState<ApplicationForm>(emptyForm);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [application, setApplication] = useState<AmbassadorApplication | null>(
    null,
  );
  const [existingProfile, setExistingProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftOwner, setDraftOwner] = useState("");
  const submitting = useRef(false);
  const formSection = useRef<HTMLDivElement>(null);
  const requestVersion = useRef(0);
  const invalidateRequests = useCallback(() => {
    requestVersion.current++;
  }, []);
  const userId = user?.id;
  const defaultName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ");
  const defaultJob = user?.job || "";
  const draftKey = `ambassador-application:draft:${userId || "guest"}`;

  useEffect(() => {
    if (authLoading) return;
    const defaults = {
      ...emptyForm,
      fullName: defaultName,
      occupation: defaultJob,
    };
    let restored = restoreForm(draftKey, defaults);
    try {
      if (userId && !sessionStorage.getItem(draftKey)) {
        restored = restoreForm("ambassador-application:draft:guest", defaults);
        sessionStorage.removeItem("ambassador-application:draft:guest");
      }
    } catch {
      /* Form remains usable without browser storage. */
    }
    setForm(restored);
    setDraftOwner(draftKey);
    setErrors({});
    setSubmitError("");
    setEditing(false);
  }, [authLoading, defaultName, defaultJob, draftKey, userId]);

  const loadApplication = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoadError("");
    setApplication(null);
    setExistingProfile(false);
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [current, context] = await Promise.all([
        ambassadors.application(),
        ambassadors.context(),
      ]);
      if (version !== requestVersion.current) return;
      setApplication(current);
      setExistingProfile(!!context.profile);
    } catch (error) {
      if (version === requestVersion.current) setLoadError(apiMessage(error));
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!authLoading) void loadApplication();
    return invalidateRequests;
  }, [authLoading, loadApplication, invalidateRequests]);

  useEffect(() => {
    if (
      draftOwner !== draftKey ||
      loading ||
      application?.status === "PENDING" ||
      application?.status === "APPROVED"
    )
      return;
    try {
      sessionStorage.setItem(
        draftKey,
        JSON.stringify({ ...form, acceptedTerms: false }),
      );
    } catch {
      /* Optional storage. */
    }
  }, [form, draftKey, draftOwner, loading, application?.status]);

  const update = <K extends keyof ApplicationForm>(
    field: K,
    value: ApplicationForm[K],
  ) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: "" }));
    setSubmitError("");
  };
  const startEditing = () => {
    if (!application) return;
    setForm({
      fullName: application.fullName,
      city: application.city,
      occupation: application.occupation || "",
      telegramUsername: application.telegramUsername || "",
      weeklyHours: application.weeklyHours,
      motivation: application.motivation,
      experience: application.experience || "",
      acceptedTerms: false,
    });
    setEditing(true);
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!user) {
      try {
        sessionStorage.setItem(
          draftKey,
          JSON.stringify({ ...form, acceptedTerms: false }),
        );
      } catch {
        /* Optional storage. */
      }
      navigate(buildLoginUrl("/ambassadors/join"));
      return;
    }
    const next: FieldErrors = {};
    if (form.fullName.trim().length < 3)
      next.fullName = "نام و نام خانوادگی کاملت را بنویس.";
    if (form.city.trim().length < 2) next.city = "شهر محل سکونتت را بنویس.";
    if (!form.weeklyHours)
      next.weeklyHours = "زمان تقریبی همراهی در هفته را انتخاب کن.";
    if (form.motivation.trim().length < 20)
      next.motivation = "کمی بیشتر از انگیزه‌ات بگو؛ حداقل ۲۰ نویسه.";
    if (!form.acceptedTerms)
      next.acceptedTerms =
        "برای ارسال درخواست، موافقتت با شرایط همراهی لازم است.";
    setErrors(next);
    if (Object.keys(next).length) {
      document.getElementById(`join-${Object.keys(next)[0]}`)?.focus();
      return;
    }
    submitting.current = true;
    const version = requestVersion.current;
    setSaving(true);
    setSubmitError("");
    try {
      const result = await ambassadors.apply({
        fullName: form.fullName.trim(),
        city: form.city.trim(),
        occupation: form.occupation.trim() || undefined,
        telegramUsername: form.telegramUsername.trim() || undefined,
        weeklyHours: form.weeklyHours as AmbassadorWeeklyHours,
        motivation: form.motivation.trim(),
        experience: form.experience.trim() || undefined,
        acceptedTerms: true,
      });
      if (version !== requestVersion.current) return;
      setApplication(result);
      setEditing(false);
      try {
        sessionStorage.removeItem(draftKey);
      } catch {
        /* Optional storage. */
      }
      formSection.current?.scrollIntoView?.({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start",
      });
    } catch (error: any) {
      if (version !== requestVersion.current) return;
      setSubmitError(apiMessage(error));
      if (error?.response?.status === 409) void loadApplication();
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };
  const scrollToForm = () =>
    formSection.current?.scrollIntoView?.({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  const fieldProps = (field: keyof ApplicationForm) => ({
    id: `join-${field}`,
    "aria-invalid": !!errors[field],
    "aria-describedby": errors[field] ? `join-error-${field}` : undefined,
  });
  const fieldError = (field: keyof ApplicationForm) =>
    errors[field] ? (
      <span className="aj-field-error" id={`join-error-${field}`}>
        {errors[field]}
      </span>
    ) : null;
  const showForm =
    !existingProfile &&
    (!application || (application.status === "REJECTED" && editing));

  return (
    <div className="aj" dir="rtl">
      <div className="aj-container">
        <nav className="aj-breadcrumb" aria-label="مسیر دسترسی">
          <Link to="/">خانه</Link>
          <ChevronLeft size={13} />
          <span>سفیران من حقیقی</span>
        </nav>
        <section className="aj-hero" aria-labelledby="join-title">
          <motion.div
            className="aj-hero-copy"
            initial={{ opacity: 0, y: reduceMotion ? 0 : 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <span className="aj-eyebrow">
              <span /> باشگاه سفیران من حقیقی
            </span>
            <h1 id="join-title">
              رشد، وقتی زیباتر است
              <br />
              که <em>با هم</em> اتفاق بیفتد.
            </h1>
            <p>
              اگر یادگیری برایت ارزشمند است و دوست داری این تجربه را با دیگران
              به اشتراک بگذاری، جای تو در جمع سفیران ما خالی است.
            </p>
            <div className="aj-hero-actions">
              <button className="aj-button" onClick={scrollToForm}>
                می‌خواهم سفیر شوم <ArrowLeft size={18} />
              </button>
              <Link to="/ambassadors" className="aj-text-link">
                قبلاً سفیر شده‌ام <ArrowUpLeft size={16} />
              </Link>
            </div>
            <div className="aj-hero-note">
              <HeartHandshake size={16} />
              <span>همراهی داوطلبانه، با انتخاب و به سبک خودت</span>
            </div>
          </motion.div>
          <div className="aj-visual" aria-hidden="true">
            <div className="aj-orbit aj-orbit-outer" />
            <div className="aj-orbit aj-orbit-inner" />
            <div className="aj-visual-center">
              <Sprout size={43} strokeWidth={1.4} />
              <strong>از من، به ما</strong>
              <span>یک قدم نزدیک‌تر به رشد</span>
            </div>
            <div className="aj-orbit-card aj-orbit-learn">
              <span className="aj-mini-icon">
                <BookOpen size={20} />
              </span>
              <div>
                <strong>یاد بگیریم</strong>
                <small>آگاهی، نقطه شروع ماست</small>
              </div>
            </div>
            <div className="aj-orbit-card aj-orbit-share">
              <span className="aj-mini-icon aj-pink">
                <Send size={20} />
              </span>
              <div>
                <strong>به اشتراک بگذاریم</strong>
                <small>روایت یک تجربه ارزشمند</small>
              </div>
            </div>
            <div className="aj-orbit-card aj-orbit-grow">
              <span className="aj-mini-icon aj-green">
                <Leaf size={20} />
              </span>
              <div>
                <strong>با هم رشد کنیم</strong>
                <small>اثرهای کوچک، مسیرهای تازه</small>
              </div>
            </div>
            <span className="aj-orbit-dot aj-dot-one" />
            <span className="aj-orbit-dot aj-dot-two" />
            <Sparkles className="aj-visual-spark" size={23} />
          </div>
        </section>

        <section className="aj-values" aria-label="ویژگی‌های برنامه سفیران">
          <div>
            <span className="aj-value-icon">
              <UsersRound size={23} />
            </span>
            <p>
              <strong>یک جمع هم‌مسیر</strong>
              <span>همراهی با علاقه‌مندان به رشد و خودشناسی</span>
            </p>
          </div>
          <div>
            <span className="aj-value-icon">
              <BookOpen size={23} />
            </span>
            <p>
              <strong>محتوای معتبر، معرفی آگاهانه</strong>
              <span>انتخاب و اشتراک‌گذاری محتوایی که به آن باور داری</span>
            </p>
          </div>
          <div>
            <span className="aj-value-icon">
              <MessageCircle size={23} />
            </span>
            <p>
              <strong>در این مسیر تنها نیستی</strong>
              <span>راهنمای شروع و همراهی تیم پشتیبانی</span>
            </p>
          </div>
        </section>

        <section className="aj-process" aria-labelledby="join-process-title">
          <div className="aj-section-caption">
            <span>مسیر پیوستن</span>
            <h2 id="join-process-title">از یک آشنایی، تا یک همراهی</h2>
          </div>
          <ol>
            {steps.map((step, index) => (
              <li key={step.title}>
                <span className="aj-step-number">
                  ۰{index + 1 === 1 ? "۱" : index + 1 === 2 ? "۲" : "۳"}
                </span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="aj-application-layout"
          aria-labelledby="join-form-title"
        >
          <aside className="aj-aside">
            <span className="aj-eyebrow">یک شروع ساده</span>
            <h2 id="join-form-title">
              داستان همراهی تو
              <br />
              از اینجا شروع می‌شود.
            </h2>
            <p>
              برای سفیر شدن، لازم نیست مخاطبان زیادی داشته باشی. علاقه به
              یادگیری، معرفی مسئولانه و تمایل به همراهی، نقطه شروع خوبی است.
            </p>
            <ul className="aj-principles">
              <li>
                <Check size={17} /> انتخاب فعالیت متناسب با وقت و علاقه تو
              </li>
              <li>
                <Check size={17} /> احترام به حریم خصوصی و رضایت مخاطب
              </li>
              <li>
                <Check size={17} /> امکان توقف یا پایان همکاری در هر زمان
              </li>
            </ul>
            <div className="aj-aside-note">
              <ShieldCheck size={25} />
              <div>
                <strong>اطلاعاتت نزد تیم ما می‌ماند</strong>
                <p>
                  اطلاعات فرم برای بررسی درخواست و هماهنگی همکاری در اختیار تیم
                  سفیران قرار می‌گیرد.
                </p>
                <Link to="/privacy">
                  حریم خصوصی <ArrowLeft size={13} />
                </Link>
              </div>
            </div>
            <div className="aj-faq">
              <h3>قبل از شروع، بدانیم</h3>
              <details>
                <summary>آیا داشتن کانال یا صفحه پرمخاطب لازم است؟</summary>
                <p>
                  خیر. در شروع نیازی به کانال یا تعداد مشخصی مخاطب نیست. مراحل
                  راه‌اندازی پس از بررسی درخواست با راهنمایی تیم انجام می‌شود.
                </p>
              </details>
              <details>
                <summary>چطور نتیجه درخواست را پیگیری کنم؟</summary>
                <p>
                  پس از ورود به حساب، همین صفحه وضعیت درخواست و نتیجه بررسی را
                  نشان می‌دهد.
                </p>
              </details>
              <details>
                <summary>آیا ارسال درخواست به معنی پذیرش قطعی است؟</summary>
                <p>
                  درخواستت ابتدا توسط تیم سفیران بررسی می‌شود. پس از تأیید،
                  مراحل آشنایی و شروع همکاری را در پنل خود می‌بینی.
                </p>
              </details>
            </div>
          </aside>

          <div className="aj-form-card" id="application" ref={formSection}>
            <div className="aj-form-heading">
              <div>
                <span className="aj-small-label">فرم آشنایی و همکاری</span>
                <h3>درخواست عضویت در جمع سفیران</h3>
              </div>
              <span className="aj-form-heading-icon">
                <HeartHandshake size={25} />
              </span>
            </div>
            {authLoading || loading ? (
              <div className="aj-state" role="status">
                <LoaderCircle className="aj-spin" size={27} />
                <h4>در حال دریافت وضعیت درخواست…</h4>
              </div>
            ) : loadError ? (
              <div className="aj-state">
                <p className="aj-error" role="alert">
                  {loadError}
                </p>
                <button className="aj-button" onClick={loadApplication}>
                  تلاش دوباره
                </button>
              </div>
            ) : !showForm ? (
              <div
                className={`aj-state aj-state-${application?.status?.toLowerCase() || "approved"}`}
                role="status"
              >
                <span className="aj-state-icon">
                  {application?.status === "PENDING" ? (
                    <Clock3 size={31} />
                  ) : application?.status === "REJECTED" ? (
                    <MessageCircle size={31} />
                  ) : (
                    <CheckCheck size={31} />
                  )}
                </span>
                <span className="aj-small-label">
                  {application?.status === "PENDING"
                    ? "در انتظار بررسی تیم"
                    : application?.status === "REJECTED"
                      ? "نتیجه بررسی درخواست"
                      : "یک قدم نزدیک‌تر به همراهی"}
                </span>
                <h4>
                  {application?.status === "PENDING"
                    ? "درخواستت به دست ما رسید."
                    : application?.status === "REJECTED"
                      ? "در این مرحله امکان شروع همکاری نیست."
                      : "به جمع سفیران خوش آمدی."}
                </h4>
                <p>
                  {application?.status === "PENDING"
                    ? "تیم سفیران اطلاعاتت را بررسی می‌کند. برای دیدن نتیجه، دوباره به همین صفحه سر بزن؛ نیازی به ثبت درخواست تازه نیست."
                    : application?.status === "REJECTED"
                      ? "پیام تیم را بخوان. اگر اطلاعات یا شرایطت تغییر کرده، می‌توانی فرم را ویرایش و دوباره ارسال کنی."
                      : "پرونده سفیری تو به حسابت متصل است. مراحل آشنایی و شروع همکاری را از پنل سفیران دنبال کن."}
                </p>
                {application && (
                  <div className="aj-request-meta">
                    <UserRound size={15} />
                    <span>{application.fullName}</span>
                    <span>ثبت در {dateLabel(application.createdAt)}</span>
                  </div>
                )}
                {application?.reviewNote && (
                  <div className="aj-review-note">
                    <strong>پیام تیم سفیران</strong>
                    <p>{application.reviewNote}</p>
                  </div>
                )}
                <div className="aj-status-actions">
                  {application?.status === "PENDING" ? (
                    <button
                      className="aj-secondary-button"
                      onClick={loadApplication}
                    >
                      بررسی دوباره وضعیت
                    </button>
                  ) : application?.status === "REJECTED" ? (
                    <button className="aj-button" onClick={startEditing}>
                      ویرایش و ارسال دوباره <ArrowLeft size={17} />
                    </button>
                  ) : (
                    <Link className="aj-button" to="/ambassadors">
                      ورود به پنل سفیران <ArrowLeft size={17} />
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={submit} aria-label="فرم درخواست عضویت" noValidate>
                {!user && (
                  <div className="aj-login-notice">
                    <LockKeyhole size={17} />
                    <p>
                      فرم را تکمیل کن؛ برای ثبت نهایی وارد حساب می‌شوی و
                      اطلاعاتت حفظ می‌شود.
                    </p>
                  </div>
                )}
                {editing && (
                  <div className="aj-login-notice">
                    <MessageCircle size={17} />
                    <p>
                      در حال ویرایش درخواست قبلی هستی. پس از ارسال، درخواست
                      دوباره بررسی می‌شود.
                    </p>
                  </div>
                )}
                <fieldset disabled={saving}>
                  <legend>
                    <span>۰۱</span> کمی از خودت بگو
                  </legend>
                  <div className="aj-fields-grid">
                    <label className="aj-field" htmlFor="join-fullName">
                      نام و نام خانوادگی <span className="aj-required">*</span>
                      <input
                        {...fieldProps("fullName")}
                        value={form.fullName}
                        onChange={(e) => update("fullName", e.target.value)}
                        maxLength={150}
                        autoComplete="name"
                        placeholder="مثلاً سارا احمدی"
                        required
                      />
                      {fieldError("fullName")}
                    </label>
                    <label className="aj-field" htmlFor="join-phone">
                      شماره موبایل حساب
                      <input
                        id="join-phone"
                        value={user?.phone || ""}
                        readOnly
                        disabled
                        dir="ltr"
                        placeholder="بعد از ورود نمایش داده می‌شود"
                        autoComplete="tel"
                      />
                      <span className="aj-field-hint">
                        <LockKeyhole size={11} /> شماره حساب برای پیگیری درخواست
                        استفاده می‌شود.
                      </span>
                    </label>
                    <label className="aj-field" htmlFor="join-city">
                      شهر محل سکونت <span className="aj-required">*</span>
                      <input
                        {...fieldProps("city")}
                        value={form.city}
                        onChange={(e) => update("city", e.target.value)}
                        maxLength={100}
                        autoComplete="address-level2"
                        placeholder="مثلاً تهران"
                        required
                      />
                      {fieldError("city")}
                    </label>
                    <label className="aj-field" htmlFor="join-occupation">
                      شغل یا زمینه فعالیت{" "}
                      <span className="aj-optional">اختیاری</span>
                      <input
                        {...fieldProps("occupation")}
                        value={form.occupation}
                        onChange={(e) => update("occupation", e.target.value)}
                        maxLength={150}
                        autoComplete="organization-title"
                        placeholder="مثلاً دانشجو، مدرس یا طراح"
                      />
                    </label>
                    <label
                      className="aj-field aj-field-full"
                      htmlFor="join-telegramUsername"
                    >
                      شناسه تلگرام <span className="aj-optional">اختیاری</span>
                      <input
                        {...fieldProps("telegramUsername")}
                        value={form.telegramUsername}
                        onChange={(e) =>
                          update("telegramUsername", e.target.value)
                        }
                        maxLength={100}
                        dir="ltr"
                        placeholder="@username"
                        autoComplete="off"
                      />
                      <span className="aj-field-hint">
                        اگر تلگرام داری، شناسه‌ات به هماهنگی مراحل بعد کمک
                        می‌کند.
                      </span>
                    </label>
                  </div>
                </fieldset>
                <fieldset disabled={saving}>
                  <legend>
                    <span>۰۲</span> از علاقه‌ات به همراهی بگو
                  </legend>
                  <div className="aj-field aj-hours-field">
                    <span id="join-hours-label">
                      تقریباً چقدر در هفته زمان داری؟{" "}
                      <span className="aj-required">*</span>
                    </span>
                    <div
                      className="aj-hours"
                      role="radiogroup"
                      aria-labelledby="join-hours-label"
                      aria-describedby={
                        errors.weeklyHours
                          ? "join-error-weeklyHours"
                          : undefined
                      }
                    >
                      {hours.map((hour, index) => (
                        <label
                          key={hour.value}
                          className={
                            form.weeklyHours === hour.value
                              ? "aj-hour-active"
                              : ""
                          }
                        >
                          <input
                            id={index === 0 ? "join-weeklyHours" : undefined}
                            type="radio"
                            name="weeklyHours"
                            value={hour.value}
                            checked={form.weeklyHours === hour.value}
                            onChange={() => update("weeklyHours", hour.value)}
                          />
                          <span>{hour.label}</span>
                          {form.weeklyHours === hour.value && (
                            <Check size={14} />
                          )}
                        </label>
                      ))}
                    </div>
                    {fieldError("weeklyHours")}
                    <span className="aj-field-hint">
                      این یک برآورد اولیه است؛ برنامه همراهی با هماهنگی تو شکل
                      می‌گیرد.
                    </span>
                  </div>
                  <label className="aj-field" htmlFor="join-motivation">
                    چرا دوست داری سفیر من حقیقی باشی؟{" "}
                    <span className="aj-required">*</span>
                    <textarea
                      {...fieldProps("motivation")}
                      value={form.motivation}
                      onChange={(e) => update("motivation", e.target.value)}
                      minLength={20}
                      maxLength={2000}
                      rows={4}
                      placeholder="از انگیزه‌ات، تجربه آشنایی با من حقیقی و اثری که دوست داری بگذاری بنویس…"
                      required
                    />
                    {fieldError("motivation")}
                    <span className="aj-character-count">
                      {form.motivation.length.toLocaleString("fa-IR")} از ۲٬۰۰۰
                      نویسه
                    </span>
                  </label>
                  <label className="aj-field" htmlFor="join-experience">
                    تجربه‌ای داری که دوست داری با ما در میان بگذاری؟{" "}
                    <span className="aj-optional">اختیاری</span>
                    <textarea
                      {...fieldProps("experience")}
                      value={form.experience}
                      onChange={(e) => update("experience", e.target.value)}
                      maxLength={2000}
                      rows={3}
                      placeholder="مثلاً تولید محتوا، کار گروهی، مدیریت کانال یا فعالیت داوطلبانه؛ نداشتن تجربه مانع درخواست نیست."
                    />
                  </label>
                </fieldset>
                <div className="aj-form-footer">
                  <label className="aj-consent" htmlFor="join-acceptedTerms">
                    <input
                      {...fieldProps("acceptedTerms")}
                      type="checkbox"
                      checked={form.acceptedTerms}
                      onChange={(e) =>
                        update("acceptedTerms", e.target.checked)
                      }
                      disabled={saving}
                    />
                    <span>
                      می‌دانم این همراهی داوطلبانه است و ثبت درخواست به معنی
                      پذیرش قطعی نیست. با استفاده از اطلاعاتم برای بررسی درخواست
                      و تماس تیم سفیران موافقم.
                    </span>
                  </label>
                  {fieldError("acceptedTerms")}
                  {submitError && (
                    <p className="aj-error" role="alert">
                      {submitError}
                    </p>
                  )}
                  <button
                    className="aj-button aj-submit"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <LoaderCircle className="aj-spin" size={18} /> در حال
                        ارسال درخواست…
                      </>
                    ) : (
                      <>
                        {user
                          ? editing
                            ? "ارسال دوباره درخواست"
                            : "ارسال درخواست عضویت"
                          : "ورود و ادامه ثبت درخواست"}{" "}
                        <ArrowLeft size={18} />
                      </>
                    )}
                  </button>
                  <p className="aj-footer-note">
                    <ShieldCheck size={13} />{" "}
                    {user
                      ? "نتیجه بررسی در همین صفحه قابل پیگیری است."
                      : "پیش‌نویس فرم تا پایان این نشست در مرورگرت نگهداری می‌شود."}
                  </p>
                </div>
              </form>
            )}
          </div>
        </section>
        <div className="aj-bottom-note">
          <HeartHandshake size={21} />
          <p>
            هر همراهی، از یک قدم کوچک شروع می‌شود.
            <strong>خوشحالیم که قدم اول را با ما برمی‌داری.</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AmbassadorJoin;
